import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'

const DOMAIN = 'dagger'

export const DAG_KEYS = {
  dags: ['dags'],
  dag: (dagId) => ['dag', dagId],
  executions: (dagId) => ['dag-executions', dagId],
  execution: (dagId, executionId) => ['dag-execution', dagId, executionId]
}

export const useListDags = () =>
  useQuery({
    queryKey: DAG_KEYS.dags,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-dags', {})
      return data
    },
    staleTime: 30_000
  })

export const useGetDag = (dagId) =>
  useQuery({
    queryKey: DAG_KEYS.dag(dagId),
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-dag', { dagId })
      return data
    },
    enabled: !!dagId
  })

export const useListDagExecutions = (dagId, { page = 1, pageSize = 25 } = {}) =>
  useQuery({
    queryKey: [...DAG_KEYS.executions(dagId), page, pageSize],
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-dag-executions', { dagId, page, pageSize })
      return data
    },
    enabled: !!dagId
  })

export const useGetExecution = (dagId, executionId, options = {}) =>
  useQuery({
    queryKey: DAG_KEYS.execution(dagId, executionId),
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-execution', { dagId, executionId })
      return data
    },
    enabled: !!dagId && !!executionId,
    ...options
  })

export const useGetDagStatus = (dagId, options = {}) =>
  useQuery({
    queryKey: ['dag-status', dagId],
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-dag-status', { dagId })
      return data
    },
    enabled: !!dagId && (options.enabled !== false),
    ...options
  })

export const useGetExecutionLogs = (executionId, options = {}) =>
  useQuery({
    queryKey: ['dag-execution-logs', executionId],
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-execution-logs', { executionId })
      return data
    },
    enabled: !!executionId,
    ...options
  })

export const useListTargets = () =>
  useQuery({
    queryKey: ['dagger-targets'],
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-dagger-targets', {})
      return data
    },
    staleTime: 60_000
  })

export const useRunDag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'run-dag', body)
      return data
    },
    onSuccess: (_, variables) => {
      if (variables.dagId) {
        queryClient.invalidateQueries({ queryKey: DAG_KEYS.executions(variables.dagId) }).catch(console.error)
      }
    }
  })
}

export const useCreateDag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'create-dag', body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DAG_KEYS.dags }).catch(console.error)
    }
  })
}

export const useUpdateDag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'update-dag', body)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: DAG_KEYS.dags }).catch(console.error)
      if (variables.dagId) {
        queryClient.invalidateQueries({ queryKey: DAG_KEYS.dag(variables.dagId) }).catch(console.error)
      }
    }
  })
}

export const useDeleteDag = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'delete-dag', body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DAG_KEYS.dags }).catch(console.error)
    }
  })
}
